import { useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
    createDomainRequest,
    deleteDomainRequest,
    fetchTeamDomains,
    setDefaultDomainRequest,
    verifyDomainRequest,
} from "../lib/api-client";
import type { CustomDomain } from "../types/api";

function TeamDomainsPage(): JSX.Element {
    const { teamId } = useParams<{ teamId: string }>();
    const queryClient = useQueryClient();

    const [newDomain, setNewDomain] = useState("");
    const [newDisplayName, setNewDisplayName] = useState("");
    const [showAddForm, setShowAddForm] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const domainsQuery = useQuery({
        queryKey: ["team-domains", teamId],
        queryFn: () => fetchTeamDomains(teamId!),
        enabled: Boolean(teamId),
    });

    const domains = domainsQuery.data?.domains ?? [];

    const createMutation = useMutation({
        mutationFn: async () => {
            if (!teamId) throw new Error("teamId missing");
            if (!newDomain.trim()) throw new Error("Domain is required");
            return createDomainRequest(teamId, {
                domain: newDomain.trim().toLowerCase(),
                displayName: newDisplayName.trim() || null,
            });
        },
        onSuccess() {
            void queryClient.invalidateQueries({ queryKey: ["team-domains", teamId] });
            setNewDomain("");
            setNewDisplayName("");
            setShowAddForm(false);
            setError(null);
        },
        onError(err: Error) {
            setError(err.message);
        },
    });

    const verifyMutation = useMutation({
        mutationFn: async (domainId: string) => {
            if (!teamId) throw new Error("teamId missing");
            return verifyDomainRequest(teamId, domainId);
        },
        onSuccess() {
            void queryClient.invalidateQueries({ queryKey: ["team-domains", teamId] });
        },
        onError(err: Error) {
            setError(err.message);
        },
    });

    const setDefaultMutation = useMutation({
        mutationFn: async (domainId: string) => {
            if (!teamId) throw new Error("teamId missing");
            return setDefaultDomainRequest(teamId, domainId);
        },
        onSuccess() {
            void queryClient.invalidateQueries({ queryKey: ["team-domains", teamId] });
        },
        onError(err: Error) {
            setError(err.message);
        },
    });

    const deleteMutation = useMutation({
        mutationFn: async (domainId: string) => {
            if (!teamId) throw new Error("teamId missing");
            return deleteDomainRequest(teamId, domainId);
        },
        onSuccess() {
            void queryClient.invalidateQueries({ queryKey: ["team-domains", teamId] });
        },
        onError(err: Error) {
            setError(err.message);
        },
    });

    const getStatusBadge = (domain: CustomDomain) => {
        const statusStyles: Record<string, React.CSSProperties> = {
            active: { background: "#dcfce7", color: "#166534" },
            verified: { background: "#dbeafe", color: "#1e40af" },
            verifying: { background: "#fef3c7", color: "#92400e" },
            pending: { background: "#f3f4f6", color: "#6b7280" },
            failed: { background: "#fee2e2", color: "#991b1b" },
        };
        return (
            <span
                style={{
                    padding: "0.25rem 0.5rem",
                    borderRadius: "9999px",
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    ...statusStyles[domain.status],
                }}
            >
                {domain.status.charAt(0).toUpperCase() + domain.status.slice(1)}
            </span>
        );
    };

    return (
        <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
            <header
                style={{
                    padding: "1.5rem 2rem",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    borderBottom: "1px solid var(--color-border)",
                    background: "var(--color-surface)",
                }}
            >
                <div>
                    <h1 style={{ fontSize: "1.5rem", margin: 0 }}>Custom Domains</h1>
                    <p style={{ margin: 0, color: "var(--color-text-muted)" }}>
                        Manage custom domains for serving forms on your own URLs.
                    </p>
                </div>
                <button
                    type="button"
                    className="button primary"
                    onClick={() => setShowAddForm(true)}
                    disabled={showAddForm}
                >
                    Add domain
                </button>
            </header>

            <main style={{ padding: "2rem", flex: 1 }}>
                {error && (
                    <div
                        style={{
                            padding: "1rem",
                            marginBottom: "1rem",
                            background: "#fee2e2",
                            color: "#991b1b",
                            borderRadius: "8px",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                        }}
                    >
                        <span>{error}</span>
                        <button
                            type="button"
                            onClick={() => setError(null)}
                            style={{ background: "none", border: "none", cursor: "pointer", fontSize: "1.2rem" }}
                        >
                            ×
                        </button>
                    </div>
                )}

                {showAddForm && (
                    <div
                        className="surface"
                        style={{ padding: "1.5rem", marginBottom: "1.5rem", borderRadius: "12px" }}
                    >
                        <h3 style={{ margin: "0 0 1rem" }}>Add Custom Domain</h3>
                        <div style={{ display: "grid", gap: "1rem", maxWidth: "500px" }}>
                            <label style={{ display: "flex", flexDirection: "column", gap: "0.35rem" }}>
                                <span style={{ fontSize: "0.85rem" }}>Domain name</span>
                                <input
                                    type="text"
                                    value={newDomain}
                                    onChange={(e) => setNewDomain(e.target.value)}
                                    placeholder="forms.example.com"
                                    style={{
                                        padding: "0.6rem 0.75rem",
                                        borderRadius: "8px",
                                        border: "1px solid var(--color-border)",
                                        fontSize: "0.9rem",
                                    }}
                                />
                            </label>
                            <label style={{ display: "flex", flexDirection: "column", gap: "0.35rem" }}>
                                <span style={{ fontSize: "0.85rem" }}>Display name (optional)</span>
                                <input
                                    type="text"
                                    value={newDisplayName}
                                    onChange={(e) => setNewDisplayName(e.target.value)}
                                    placeholder="My Custom Forms"
                                    style={{
                                        padding: "0.6rem 0.75rem",
                                        borderRadius: "8px",
                                        border: "1px solid var(--color-border)",
                                        fontSize: "0.9rem",
                                    }}
                                />
                            </label>
                            <div style={{ display: "flex", gap: "0.75rem" }}>
                                <button
                                    type="button"
                                    className="button primary"
                                    onClick={() => createMutation.mutate()}
                                    disabled={createMutation.isPending || !newDomain.trim()}
                                >
                                    {createMutation.isPending ? "Adding…" : "Add domain"}
                                </button>
                                <button
                                    type="button"
                                    className="button ghost"
                                    onClick={() => {
                                        setShowAddForm(false);
                                        setNewDomain("");
                                        setNewDisplayName("");
                                    }}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {domainsQuery.isLoading ? (
                    <p style={{ color: "var(--color-text-muted)" }}>Loading domains…</p>
                ) : domains.length === 0 ? (
                    <div
                        className="surface"
                        style={{
                            padding: "3rem",
                            textAlign: "center",
                            borderRadius: "12px",
                        }}
                    >
                        <h3 style={{ margin: "0 0 0.5rem" }}>No custom domains yet</h3>
                        <p style={{ color: "var(--color-text-muted)", margin: 0 }}>
                            Add a custom domain to serve forms on your own URLs.
                        </p>
                    </div>
                ) : (
                    <div style={{ display: "grid", gap: "1rem" }}>
                        {domains.map((domain) => (
                            <div
                                key={domain.id}
                                className="surface"
                                style={{
                                    padding: "1.25rem",
                                    borderRadius: "12px",
                                    display: "grid",
                                    gap: "1rem",
                                }}
                            >
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                                    <div>
                                        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                                            <h4 style={{ margin: 0, fontSize: "1.1rem" }}>{domain.domain}</h4>
                                            {getStatusBadge(domain)}
                                            {domain.isDefault && (
                                                <span
                                                    style={{
                                                        padding: "0.25rem 0.5rem",
                                                        borderRadius: "9999px",
                                                        fontSize: "0.75rem",
                                                        fontWeight: 500,
                                                        background: "#dbeafe",
                                                        color: "#1e40af",
                                                    }}
                                                >
                                                    Default
                                                </span>
                                            )}
                                        </div>
                                        {domain.displayName && (
                                            <p style={{ margin: "0.25rem 0 0", color: "var(--color-text-muted)", fontSize: "0.85rem" }}>
                                                {domain.displayName}
                                            </p>
                                        )}
                                    </div>
                                    <div style={{ display: "flex", gap: "0.5rem" }}>
                                        {domain.status === "pending" && (
                                            <button
                                                type="button"
                                                className="button small"
                                                onClick={() => verifyMutation.mutate(domain.id)}
                                                disabled={verifyMutation.isPending}
                                            >
                                                {verifyMutation.isPending ? "Verifying…" : "Verify"}
                                            </button>
                                        )}
                                        {domain.status === "active" && !domain.isDefault && (
                                            <button
                                                type="button"
                                                className="button small ghost"
                                                onClick={() => setDefaultMutation.mutate(domain.id)}
                                                disabled={setDefaultMutation.isPending}
                                            >
                                                Set default
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            className="button small ghost"
                                            style={{ color: "var(--color-critical)" }}
                                            onClick={() => {
                                                if (confirm(`Delete domain "${domain.domain}"? This cannot be undone.`)) {
                                                    deleteMutation.mutate(domain.id);
                                                }
                                            }}
                                            disabled={deleteMutation.isPending}
                                        >
                                            Delete
                                        </button>
                                    </div>
                                </div>

                                {domain.status === "pending" && domain.verificationToken && (
                                    <div
                                        style={{
                                            padding: "1rem",
                                            background: "var(--color-surface-alt, #f8fafc)",
                                            borderRadius: "8px",
                                            fontSize: "0.85rem",
                                        }}
                                    >
                                        <strong>DNS Verification Required</strong>
                                        <p style={{ margin: "0.5rem 0", color: "var(--color-text-muted)" }}>
                                            Add this TXT record to your DNS configuration:
                                        </p>
                                        <code
                                            style={{
                                                display: "block",
                                                padding: "0.75rem",
                                                background: "#1e293b",
                                                color: "#e2e8f0",
                                                borderRadius: "6px",
                                                fontFamily: "monospace",
                                                fontSize: "0.8rem",
                                                wordBreak: "break-all",
                                            }}
                                        >
                                            _rwaforms-verify.{domain.domain} IN TXT "{domain.verificationToken}"
                                        </code>
                                    </div>
                                )}

                                <div style={{ display: "flex", gap: "1.5rem", fontSize: "0.8rem", color: "var(--color-text-muted)" }}>
                                    <span>Added: {new Date(domain.createdAt).toLocaleDateString()}</span>
                                    {domain.verifiedAt && (
                                        <span>Verified: {new Date(domain.verifiedAt).toLocaleDateString()}</span>
                                    )}
                                    <span>SSL: {domain.sslStatus}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </main>
        </div>
    );
}

export default TeamDomainsPage;
